/**
 * Analysis Dashboard Page.
 *
 * Full-featured dashboard showing:
 * - Overall progress ring
 * - Per-stage progress cards (EXIF, Quality, Faces, Captions, etc.)
 * - ETA estimation with countdown
 * - Failed photos list with retry button
 * - Start analysis button
 * - Real-time updates via WebSocket
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  Play,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Layers,
} from 'lucide-react';

import {
  useAnalysisStatus,
  useStartAnalysis,
  useRetryFailed,
} from '@/api/hooks/useAnalysis';
import { useEvents } from '@/api/hooks/useEvents';
import { useWebSocket } from '@/websocket/WebSocketProvider';
import { AnalysisProgressRing } from '@/components/analysis/AnalysisProgressRing';
import { AnalysisStageCard, type StageCardStatus } from '@/components/analysis/AnalysisStageCard';
import { AnalysisETA } from '@/components/analysis/AnalysisETA';

// ---------------------------------------------------------------------------
// Stage display config
// ---------------------------------------------------------------------------

// Maps backend snake_case stage keys → i18n translation keys
const STAGE_I18N_KEYS: Record<string, string> = {
  exif_extraction: 'analysis.stages.exifExtraction',
  quality_analysis: 'analysis.stages.qualityAnalysis',
  face_detection: 'analysis.stages.faceDetection',
  face_clustering: 'analysis.stages.faceClustering',
  similarity_detection: 'analysis.stages.similarityDetection',
  phase_classification: 'analysis.stages.phaseClassification',
  best_pick_selection: 'analysis.stages.bestPickSelection',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AnalysisDashboardPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEventId = searchParams.get('event') ?? '';

  // Event selector
  const { data: eventsData } = useEvents();
  const events = useMemo(
    () => eventsData?.results ?? (eventsData as unknown as unknown[]) ?? [],
    [eventsData],
  );

  // Analysis status for selected event
  const {
    data: analysisStatus,
    isLoading,
    isError,
  } = useAnalysisStatus(selectedEventId || undefined);

  const startMutation = useStartAnalysis();
  const retryMutation = useRetryFailed();

  // WebSocket subscription for real-time updates
  const { subscribeToEvent, unsubscribeFromEvent } = useWebSocket();

  useEffect(() => {
    if (selectedEventId) {
      subscribeToEvent(selectedEventId);
      return () => unsubscribeFromEvent(selectedEventId);
    }
  }, [selectedEventId, subscribeToEvent, unsubscribeFromEvent]);

  // Derived data
  const progress = analysisStatus?.progress;
  const stages = analysisStatus?.stages ?? [];
  const overallPct = progress?.percentage ?? 0;
  const isRunning = analysisStatus?.status === 'running';
  const isCompleted = analysisStatus?.status === 'completed';
  const isIdle = !analysisStatus || analysisStatus.status === 'idle';
  const hasError = analysisStatus?.status === 'error';

  // Calculate ETA from stage data
  const etaSeconds = useMemo(() => {
    if (!isRunning || !progress) return null;
    const { processed_photos, total_photos } = progress;
    if (processed_photos === 0 || !analysisStatus?.started_at) return null;

    const elapsed =
      (Date.now() - new Date(analysisStatus.started_at).getTime()) / 1000;
    const rate = processed_photos / elapsed;
    const remaining = total_photos - processed_photos;
    return rate > 0 ? Math.round(remaining / rate) : null;
  }, [isRunning, progress, analysisStatus?.started_at]);

  // Failed photos count
  const failedCount = stages.filter((s) => s.status === 'error').length;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="page-title">{t('analysis.title', 'Analysis Dashboard')}</h1>

        {/* Event selector */}
        <select
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          value={selectedEventId}
          onChange={(e) => {
            const id = e.target.value;
            if (id) {
              setSearchParams({ event: id });
            } else {
              setSearchParams({});
            }
          }}
        >
          <option value="">{t('analysis.selectEvent', 'Select an event...')}</option>
          {(events as { id: string; title: string }[]).map((evt) => (
            <option key={evt.id} value={evt.id}>
              {evt.title}
            </option>
          ))}
        </select>
      </div>

      {/* Empty state */}
      {!selectedEventId && (
        <div className="mt-12 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
          <Layers className="h-12 w-12" />
          <p className="text-sm">{t('analysis.selectEventPrompt', 'Select an event to view analysis status.')}</p>
        </div>
      )}

      {/* Dashboard content */}
      {selectedEventId && (
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Left column: stages + ETA */}
          <div className="space-y-6 lg:col-span-2">
            {/* Actions bar */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                className="btn-primary inline-flex items-center gap-2"
                disabled={isRunning || startMutation.isPending}
                onClick={() =>
                  startMutation.mutate({ eventId: selectedEventId })
                }
              >
                <Play className="h-4 w-4" />
                {isRunning
                  ? t('analysis.running', 'Running...')
                  : t('analysis.start', 'Start Analysis')}
              </button>

              {failedCount > 0 && (
                <button
                  className="btn-outline inline-flex items-center gap-2 text-red-600 dark:text-red-400"
                  disabled={retryMutation.isPending}
                  onClick={() => retryMutation.mutate(selectedEventId)}
                >
                  <RefreshCw className="h-4 w-4" />
                  {t('analysis.retryFailed', 'Retry Failed ({{count}})', {
                    count: failedCount,
                  })}
                </button>
              )}

              {isRunning && <AnalysisETA etaSeconds={etaSeconds} />}
            </div>

            {/* Stage cards */}
            <div className="card">
              <div className="mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t('analysis.pipelineStages', 'Pipeline Stages')}
                </h2>
              </div>

              {isLoading && (
                <p className="text-sm text-gray-400">{t('status.loading')}</p>
              )}

              {isError && (
                <p className="text-sm text-red-500">
                  {t('analysis.loadError', 'Failed to load analysis status.')}
                </p>
              )}

              {!isLoading && !isError && stages.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {stages.map((stage) => {
                    const total = progress?.total_photos ?? 0;
                    const completed = Math.round(
                      (stage.progress / 100) * total,
                    );
                    return (
                      <AnalysisStageCard
                        key={stage.stage}
                        name={t(STAGE_I18N_KEYS[stage.stage] ?? stage.stage)}
                        status={stage.status as StageCardStatus}
                        completed={completed}
                        total={total}
                      />
                    );
                  })}
                </div>
              )}

              {/* Fallback stage list when no real data yet */}
              {!isLoading && !isError && stages.length === 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {Object.entries(STAGE_I18N_KEYS).map(([key, i18nKey]) => (
                    <AnalysisStageCard
                      key={key}
                      name={t(i18nKey)}
                      status="pending"
                      completed={0}
                      total={0}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right column: summary ring + status */}
          <div className="space-y-6">
            {/* Progress ring */}
            <div className="card flex flex-col items-center gap-4">
              <h2 className="w-full font-semibold text-gray-900 dark:text-gray-100">
                {t('analysis.overallProgress', 'Overall Progress')}
              </h2>
              <AnalysisProgressRing
                percentage={overallPct}
                label={
                  progress
                    ? `${progress.processed_photos} / ${progress.total_photos}`
                    : undefined
                }
                size={140}
              />

              {/* Status badge */}
              <div className="flex items-center gap-2">
                {isCompleted && (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      {t('analysis.completed', 'Analysis Complete')}
                    </span>
                  </>
                )}
                {isRunning && (
                  <>
                    <div className="h-3 w-3 animate-pulse rounded-full bg-blue-500" />
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {t('analysis.inProgress', 'In Progress')}
                    </span>
                  </>
                )}
                {hasError && (
                  <>
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">
                      {analysisStatus?.error_message ??
                        t('analysis.errorOccurred', 'An error occurred')}
                    </span>
                  </>
                )}
                {isIdle && (
                  <span className="text-sm text-gray-400 dark:text-gray-500">
                    {t('analysis.idle', 'Idle - press Start to begin')}
                  </span>
                )}
              </div>
            </div>

            {/* Quick stats */}
            <div className="card space-y-3">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                {t('analysis.summary', 'Summary')}
              </h2>

              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t('analysis.totalPhotos', 'Total Photos')}
                  </dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100">
                    {progress?.total_photos ?? 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t('analysis.processed', 'Processed')}
                  </dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100">
                    {progress?.processed_photos ?? 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t('analysis.currentStage', 'Current Stage')}
                  </dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100">
                    {analysisStatus?.current_stage
                      ? t(STAGE_I18N_KEYS[analysisStatus.current_stage] ?? analysisStatus.current_stage)
                      : '-'}
                  </dd>
                </div>
                {analysisStatus?.started_at && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">
                      {t('analysis.startedAt', 'Started At')}
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(analysisStatus.started_at).toLocaleTimeString()}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
